import { useEffect, useState } from "react";
import { Paperclip } from "lucide-react";
import { api } from "../api.js";

const FILTERS = [
  { id: "alle", label: "Alle" },
  { id: "neu", label: "Neu" },
  { id: "kontaktiert", label: "Kontaktiert" },
  { id: "umgewandelt", label: "Umgewandelt" },
];

const STATUS_LABEL = { neu: "Neu", kontaktiert: "Kontaktiert", umgewandelt: "Umgewandelt", abgesagt: "Abgesagt" };

function StatusBadge({ status }) {
  return <span className={`dx-badge dx-badge--${status}`}>{STATUS_LABEL[status] || status}</span>;
}

export default function AnfragenScreen() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("alle");
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiDraft, setAiDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.requests().then((rows) => {
      setItems(rows);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const visible = filter === "alle" ? items : items.filter((r) => r.status === filter);
  const selected = items.find((r) => r.id === selectedId) || visible[0];

  useEffect(() => {
    if (selected) setAiDraft(selected.ai_reply || "");
  }, [selected?.id]);

  const act = async (fn) => {
    setBusy(true);
    try {
      await fn();
      load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dx-two-col">
      <div className="dx-card dx-list-col">
        <div className="dx-filter-row">
          {FILTERS.map((f) => (
            <button key={f.id} className={"dx-filter" + (filter === f.id ? " dx-filter--active" : "")} onClick={() => setFilter(f.id)}>
              {f.label}
            </button>
          ))}
        </div>
        {loading ? <p className="dx-muted">Lade…</p> : null}
        {!loading && visible.length === 0 ? <p className="dx-muted">Keine Anfragen.</p> : null}
        <div className="dx-list">
          {visible.map((r) => (
            <button key={r.id} className={"dx-list-item" + (selected?.id === r.id ? " dx-list-item--active" : "")} onClick={() => setSelectedId(r.id)}>
              <div className="dx-list-item-top">
                <strong>{r.customer_name}</strong>
                <StatusBadge status={r.status} />
              </div>
              <div className="dx-muted" style={{ fontSize: "13px" }}>{r.product} {r.size ? `· ${r.size}` : ""}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="dx-card dx-detail-col">
        {!selected ? (
          <p className="dx-muted">Anfrage auswählen.</p>
        ) : (
          <>
            <div className="dx-detail-header">
              <h2>{selected.customer_name}</h2>
              <StatusBadge status={selected.status} />
            </div>
            <div className="dx-detail-grid">
              <div><span className="dx-label">E-Mail</span><div>{selected.email}</div></div>
              <div><span className="dx-label">Telefon</span><div>{selected.phone || "-"}</div></div>
              <div><span className="dx-label">Firma</span><div>{selected.company || "-"}</div></div>
              <div><span className="dx-label">Produkt</span><div>{selected.product}</div></div>
              <div><span className="dx-label">Größe</span><div>{selected.size || "-"}</div></div>
              <div><span className="dx-label">LED</span><div>{selected.led ? "Ja" : "Nein"}</div></div>
              {selected.qty ? <div><span className="dx-label">Menge</span><div>{selected.qty}</div></div> : null}
              <div><span className="dx-label">Geschätzter Preis</span><div>{selected.estimate ? `${selected.estimate} €` : "-"}</div></div>
            </div>
            {selected.file_url ? (
              <a href={selected.file_url} target="_blank" rel="noopener" className="dx-file-link"><Paperclip size={14} /> {selected.file_name || "Datei ansehen"}</a>
            ) : null}
            <div>
              <span className="dx-label">Kundennachricht</span>
              <p style={{ whiteSpace: "pre-wrap" }}>{selected.notes || "-"}</p>
            </div>
            <div>
              <span className="dx-label">KI-Antwortentwurf</span>
              <textarea
                className="dx-textarea"
                rows={5}
                value={aiDraft}
                onChange={(e) => setAiDraft(e.target.value)}
                placeholder="Noch kein Entwurf. KI-Anbindung folgt separat - Text hier manuell eintragen und speichern."
              />
              <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                <button className="dx-btn dx-btn--ghost" onClick={() => act(() => api.setAiReply(selected.id, aiDraft))} disabled={busy}>
                  Speichern
                </button>
                <button className="dx-btn dx-btn--ghost" onClick={() => navigator.clipboard.writeText(aiDraft)}>
                  Kopieren
                </button>
              </div>
            </div>
            <div className="dx-action-row">
              <button className="dx-btn dx-btn--primary" disabled={busy || selected.status === "umgewandelt"} onClick={() => act(() => api.convertRequest(selected.id))}>
                In Auftrag umwandeln
              </button>
              <button className="dx-btn dx-btn--ghost" disabled={busy || selected.status !== "neu"} onClick={() => act(() => api.markContacted(selected.id))}>
                Als kontaktiert markieren
              </button>
              <button className="dx-btn dx-btn--danger" disabled={busy || selected.status === "abgesagt"} onClick={() => act(() => api.cancelRequest(selected.id))}>
                Absagen
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
